import moment from "moment";
import Swal from "sweetalert2";
import messageService from "../services/message/messageService";
import { uploadImage1 } from "../services/Axios/Axios";
import { ApiUrl } from "../config/apiConfig";
import { handleError } from "./handleError";

const formatDate = (dateString: moment.MomentInput, dateFormat = "MMMM DD, YYYY, HH:mm") => {
    if (!dateString) return "";
    return moment(dateString).format(dateFormat);
};

function formatDateInUS(isoDate: string): string {
  const date = new Date(isoDate);

  // options for formatting
  const options: Intl.DateTimeFormatOptions = {
    month: "short",   // Sep
    day: "2-digit",   // 06
    year: "numeric",  // 2025
    hour: "numeric",  // 3
    minute: "2-digit", // 57
    hour12: true,     // AM/PM
  };

  return date.toLocaleString("en-US", options);
}

function getPhoneCode(countryName:string) {
    const phoneCodes:any = {
      "united states": "+1",
      "canada": "+1",
      "india": "+91"
    };
  
    const normalized = countryName.trim().toLowerCase();
  
    return phoneCodes[normalized] || "+1";
  }

  /**
 * Sort an array of  objects by createdAt (descending)
 */
function sortByCreatedAtDesc(admissions: any[]): any[] {
  return [...admissions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/**
 * Shows a confirmation alert with a message.
 * Executes the callback if user confirms.
 */
function confirmAction(message: string, onConfirm: () => void): void {
  Swal.fire({
      title: "Are you sure?",
      text: message,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
  }).then((result) => {
      if (result.isConfirmed) {
          onConfirm();
      }
  });
}

function generatePassword(length: number = 12): string {
  if (length < 9 || length > 50) {
    throw new Error("Password length must be between 9 and 50 characters");
  }

  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const special = "&*%^$_#*";
  const allChars = upper + lower + numbers + special;

  const passwordArray: string[] = [];

  // ✅ Ensure each rule is satisfied
  passwordArray.push(getRandomChar(upper));
  passwordArray.push(getRandomChar(lower));
  passwordArray.push(getRandomChar(numbers));
  passwordArray.push(getRandomChar(special));

  // ✅ Fill remaining length
  for (let i = passwordArray.length; i < length; i++) {
    passwordArray.push(getRandomChar(allChars));
  }

  // ✅ Secure shuffle (Fisher-Yates)
  shuffleArray(passwordArray);

  return passwordArray.join("");
}

function getRandomChar(chars: string): string {
  const array = new Uint8Array(1);
  window.crypto.getRandomValues(array);
  return chars[array[0] % chars.length];
}

function shuffleArray(arr: string[]) {
  for (let i = arr.length - 1; i > 0; i--) {
    const rand = new Uint8Array(1);
    window.crypto.getRandomValues(rand);
    const j = rand[0] % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

const getRemainingTrialDays = (startDate: string, days: number = 10) => {
  const start = new Date(startDate);

  // expiry date (local)
  const expire = new Date(start.getFullYear(), start.getMonth(), start.getDate() + days);

  // today (local)
  const today = new Date();
  const localToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const diffMs = expire.getTime() - localToday.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return diffDays < 0 ? 0 : diffDays;
};

const getCurrentUser = () => {
  const user = localStorage.getItem("userData");
  if (!user) return null;
  return JSON.parse(user);
};


function showSuccessToast(message: string) {
  const messageServices = messageService();
  messageServices.toast("success", message);
}

function showErrorToast(message: string) {
  const messageServices = messageService();

  messageServices.toast("error", message);
}

const formatPhone = (phone?: string): string => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

const formatDateForInput = (value?: string | number) => {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
};

const uploadFile = async (uploadedFile: File): Promise<string | null> => {
  if (!uploadedFile) return null;

  try {
      const response = await uploadImage1(ApiUrl.fileUpload, uploadedFile);

      if (response?.original) {
          return response.original as string;
      }
      return null;
  } catch (error) {
      handleError(error);
      return null;
  }
};


const sourceColors: Record<string, string> = {
  IFTA: "text-[#783d67] bg-purple-200",
  Main: "text-[#115fe4] bg-blue-200",
  App:"bg-[#deace3]/30 text-[#d811ea]"
};

function getSourceColor(source: string): string {
  return sourceColors[source] || "bg-gray-400";
}

function isExpireDate(timestamp: number): boolean {
  if (timestamp === 0) return false;
  const now = Date.now(); // current timestamp in ms
  return timestamp < now;
}


function formatDateInUSFormatByDate(date: Date): string {
  // options for formatting
  const options: Intl.DateTimeFormatOptions = {
      month: "short", // Sep
      day: "2-digit", // 06
      year: "numeric", // 2025
      hour: "numeric", // 3
      minute: "2-digit", // 57
      hour12: true, // AM/PM
  };

  return date.toLocaleString("en-US", options);
}


function capitalize(text?: string | null): string {
  if (!text) return "";

  const lower = text.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export {getRemainingTrialDays,uploadFile,getSourceColor, formatDateForInput, showSuccessToast, showErrorToast, getCurrentUser,formatDate ,getPhoneCode,formatDateInUS,sortByCreatedAtDesc ,confirmAction,generatePassword,formatPhone,isExpireDate,formatDateInUSFormatByDate,capitalize};
